import React, { useCallback, useMemo } from "react";

import { ReviewList as ReviewListType } from "@design-controls/types";
import {
  createQColumnHelper,
  DataProvider,
  Filtering,
  Pagination,
  QDataTable,
  Sorting,
} from "@qualio/ui-components";
import { useTranslation } from "react-i18next";

import { useRetrieveReviews } from "../../hooks/useRetrieveReviews";
import { Owner } from "./filter/Owner";
import { Status } from "./filter/Status";
import { useRetrieveUsers } from "../../hooks/useRetrieveUsers";
import { Link } from "react-router-dom";
import { useCurrentProduct } from "../../hooks/useCurrentProduct";

const columnHelper = createQColumnHelper<ReviewListType>();

export const ReviewList: React.FC = () => {
  const { t } = useTranslation();
  const { isLoading, data } = useRetrieveReviews();
  const { id: productId } = useCurrentProduct();
  const { isLoading: usersIsLoading, data: users } = useRetrieveUsers();

  const StatusTable: Record<string, string> = useMemo(
    () => ({
      IN_PROGRESS: t("statuses.review.draft"),
      SENT_FOR_APPROVAL: t("statuses.review.forApproval"),
      APPROVED: t("statuses.review.approved"),
      NOT_APPROVED: t("statuses.review.declined"),
    }),
    [t],
  );

  const columns = useMemo(() => {
    return [
      columnHelper.code("code", {
        header: t("reviews.list.table.header.id"),
        maxWidth: "100px",
      }),
      columnHelper.custom(
        (record) => record.title ?? t("reviews.list.untitledReview"),
        {
          header: t("reviews.list.table.header.reviewName"),
          weight: 1,
          enableSorting: true,
          cell: (props) => {
            return <Link to={`${props.row.id}/view`}>{props.getValue()}</Link>;
          },
        },
      ),
      columnHelper.text((record) => record.createdBy?.fullName, {
        header: t("reviews.list.table.header.owner"),
      }),
      columnHelper.date((record) => new Date(record.updated), {
        header: t("reviews.list.table.header.lastModified"),
        id: "updated",
        maxWidth: "150px",
      }),
      columnHelper.status((record) => StatusTable[record.status], {
        header: t("reviews.list.table.header.status"),
        maxWidth: "150px",
        statuses: {
          [t("statuses.review.draft")]: "gray",
          [t("statuses.review.forApproval")]: "blue",
          [t("statuses.review.approved")]: "green",
          [t("statuses.review.declined")]: "red",
        },
      }),
    ];
  }, [t, StatusTable]);

  const filterDefinitions = useMemo(() => {
    return {
      owner: {
        label: t("reviews.list.filters.owner"),
        schema: Filtering.schemas.StringSchema().nullish(),
        activeRender: (v: any) => ({
          label: t("reviews.list.filterLabels.owner"),
          value: v && users?.find((item) => item.id === Number(v))?.fullName,
        }),
      },
      status: {
        label: t("reviews.list.filters.status"),
        schema: Filtering.schemas.StringSchema().nullish(),
        activeRender: (v: any) => ({
          label: t("reviews.list.filterLabels.status"),
          value: v && StatusTable[v],
        }),
      },
    };
  }, [users, StatusTable, t]);

  const filterOption = useCallback(
    (
      item: Readonly<ReviewListType>,
      searchTerm: string | undefined,
      filters: Filtering.ResolvedFilters<typeof filterDefinitions> | undefined,
    ) => {
      if (
        searchTerm &&
        !item.title
          ?.toLocaleLowerCase()
          .includes(searchTerm.toLocaleLowerCase()) &&
        !item.code.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
      ) {
        return false;
      }

      if (filters && Object.values(filters).some((field) => field.value)) {
        const { owner, status } = filters;

        return (
          (!owner?.value || item.createdBy?.id === owner.value) &&
          (!status?.value || item.status === status.value)
        );
      }

      return true;
    },
    [],
  ) as DataProvider.FilterOptionFn<ReviewListType>;

  return (
    <Pagination.Auto pageSizeKey="size" pageIndexKey="page" clientSide>
      <Sorting.DefaultSortingProvider
        initialSort={{ column: "updated", descending: true }}
      >
        <Filtering.FilterProvider
          definitions={filterDefinitions}
          searchTermKey="search"
          persistanceKey={`review-list-filters-${productId}`}
        >
          <DataProvider.Fixed
            data={data ?? []}
            isLoading={isLoading}
            filterOption={filterOption}
          >
            <QDataTable
              columns={columns}
              data-cy="review-list"
              getRowId={(row) => row.id}
            >
              <Filtering.FormContent>
                <Owner isLoading={usersIsLoading} data={users} />
                <Status />
              </Filtering.FormContent>
            </QDataTable>
          </DataProvider.Fixed>
        </Filtering.FilterProvider>
      </Sorting.DefaultSortingProvider>
    </Pagination.Auto>
  );
};
